// Real, per-model surveillance camera hardware — a MULTI-BRAND catalog,
// deliberately separate from unifiCatalog.js (which is Ubiquiti-only by
// design). Added 2026-07-30 per real staff direction: "pull all the REOLINK
// cameras including their Doorbells... In time I want to collect most of
// the brands with pricing and model #." — this file is the home for every
// future brand's real per-model camera pricing, not just Reolink.
//
// Mirrors the wap_model/UNIFI_CATALOG and access_model/
// UNIFI_ACCESS_READER_CATALOG pattern exactly: `camera_model` on
// buildlab_floorplan_devices (freeform text, NOT CHECK-constrained — a new
// brand/model never needs a migration) is matched against this array's
// `model` key. `camera_subtype` (bullet/dome/fisheye_360/panoramic_180/ptz/
// multi_sensor/doorbell/other — DB CHECK-constrained) still drives which
// glyph/coverage-cone shape renders; `subtype` here is what lets the
// DeviceEditPanel.js model picker show only the models matching whichever
// subtype button is currently pressed, so picking "PTZ" never offers a
// bullet-shaped model. estimateDeviceCost() (lowVoltageRates.js) prices a
// modeled camera from this array's real `priceUsd` (hardware only) while
// STILL deriving labor from DEVICE_RATES.camera[subtype].labor — mounting/
// aim-and-focus labor is about the camera's physical FORM, not its brand,
// so a Reolink PTZ and a UniFi PTZ take the same real install time even
// though their hardware costs differ.
//
// `poe` is true for every entry here on purpose — this catalog assumes a
// per-device home-run cable (see CAMERA_CABLE_RUN in lowVoltageRates.js),
// so a battery/WiFi/solar camera doesn't fit this pricing model at all (no
// cable run to bill, no PoE budget to plan against). Reolink's WiFi/battery
// Argus line and WiFi-only doorbell were deliberately excluded from
// research for this reason, not overlooked.
//
// `fovDegDiagonal` feeds DeviceEditPanel.js's "picking a model snaps FOV to
// its real spec" behavior — left `null` wherever a model's diagonal FOV
// isn't actually published (never a computed/estimated guess from H/V specs)
// AND, separately, deliberately left `null` for any full-circle subtype
// (fisheye_360, multi_sensor) even when a real diagonal IS published (e.g.
// UVC-G6-Pro-360's real ~180° hemisphere spec) — this app's `fov_deg: 360`
// for those subtypes is a RENDERING SENTINEL (`FULL_CIRCLE_THRESHOLD` in
// DeviceMarker.js, not a literal optical angle) that draws a full circle;
// auto-applying the real ~180° optical spec would drop below that threshold
// and silently break full-circle rendering for anyone who picks that model.
// PTZ entries use the WIDE (no-zoom) end of a zoom-range FOV spec, matching
// this app's existing single-static-number treatment of camera FOV (it
// doesn't model zoom state at all).
//
// Every imageUrl below was independently confirmed (HTTP 200, image/*
// content-type) before being added — same never-fabricate convention as
// every other real-hardware catalog in this codebase. `null` means no
// genuinely verifiable real product photo was found (e.g. Reolink's own CDN
// served some PTZ images as application/octet-stream instead of image/*, or
// only a features/spec graphic existed, not a plain product photo) — never
// a guessed/unverified URL.
export const CAMERA_CATALOG = [
    // --- Ubiquiti UniFi Protect G6 line -------------------------------
    // Real per-model SKUs behind the flat prices already staff-supplied
    // into DEVICE_RATES.camera (bullet/dome/ptz/panoramic_180/fisheye_360)
    // — confirmed 2026-07-30 via store.ui.com's own product JSON-LD +
    // techspecs.ui.com, all 5 prices match the existing figures exactly (no
    // drift), so adding these here doesn't change anything for a device
    // left unmodeled — it just lets staff explicitly pick the real SKU and
    // get its own tally row / product photo instead of the generic subtype
    // bucket. Images reuse the same already-curl-verified URLs from
    // CAMERA_SUBTYPE_IMAGES above (identical products, no need to re-fetch).
    {
        brand: 'Ubiquiti', model: 'UVC-G6-Bullet', product: 'Camera G6 Bullet', subtype: 'bullet',
        priceUsd: 199, poe: true, fovDegDiagonal: 134.1,
        specs: '8MP, 3840×2160 — diagonal FOV 134.1°.',
        imageUrl: 'https://cdn.ecomm.ui.com/products/5dc40311-0e08-4eaa-b901-472cc707b436/c7746e68-2298-43ce-9793-ea8b1bc20f97.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },
    {
        brand: 'Ubiquiti', model: 'UVC-G6-Dome', product: 'Camera G6 Dome', subtype: 'dome',
        priceUsd: 279, poe: true, fovDegDiagonal: 134.1,
        specs: '8MP, 3840×2160 — diagonal FOV 134.1° (techspecs publishes the same figure as G6 Bullet — appears to share the same optics module).',
        imageUrl: 'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/177298b2-ba1d-479f-a16f-172e4eb0970a.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },
    {
        brand: 'Ubiquiti', model: 'UVC-G6-PTZ', product: 'Camera G6 PTZ', subtype: 'ptz',
        priceUsd: 399, poe: true, fovDegDiagonal: 134.1,
        specs: '8MP, 3840×2160 — 10x hybrid zoom, diagonal FOV 134.1° wide / 30.4° full telephoto.',
        imageUrl: 'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/e38c2682-4417-4e12-8a55-71c8cbc2b45d.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },
    {
        brand: 'Ubiquiti', model: 'UVC-G6-180', product: 'Camera G6 180', subtype: 'panoramic_180',
        priceUsd: 299, poe: true, fovDegDiagonal: 180,
        specs: '16MP, 7680×2160 (3.5:1 panoramic) — FOV horizontal 180° / vertical 56.7° (no single diagonal spec published; 180 used to match this app\'s own panoramic_180 convention). White colorway currently out of stock; Black in stock.',
        imageUrl: 'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/d6585481-ad64-4563-9b1e-5a0a0822613c.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },
    {
        brand: 'Ubiquiti', model: 'UVC-G6-Pro-360', product: 'Camera G6 Pro 360', subtype: 'fisheye_360',
        priceUsd: 499, poe: true, fovDegDiagonal: null, // full-circle sentinel — see file header, do NOT set to the real ~180° hemisphere spec
        specs: '12MP, 3504×3504 (1:1) fisheye — real optical FOV ~180° hemisphere (this app renders fisheye_360 as a full circle via fov_deg:360, a rendering sentinel, not this literal spec). White colorway currently out of stock; Black in stock.',
        imageUrl: 'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/50fc1f10-c18b-4c1a-913d-59d2b616f558.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },
    {
        brand: 'Ubiquiti', model: 'UVC-G4-Doorbell-Pro-PoE-Kit', product: 'G4 Doorbell Pro PoE Kit', subtype: 'doorbell',
        priceUsd: 379, poe: true, fovDegDiagonal: 155,
        specs: '2MP main cam (1600×1200) + 8MP package cam — diagonal FOV 155° (LDC on) / 160° (LDC off) main, 118.2° package cam. Bundles the required PoE-to-24VAC adapter + a UACC-Chime-PoE indoor chime. Currently OUT OF STOCK (both colors) per store.ui.com\'s live availability field — confirm lead time before quoting.',
        imageUrl: 'https://cdn.ecomm.ui.com/products/217c2677-ceb5-4908-8953-792cdc72fd32/d5552edd-a6a5-4cbc-8fec-e05070faa882.png',
        source: 'store.ui.com / techspecs.ui.com, confirmed 2026-07-30',
    },

    // --- Reolink PoE line ----------------------------------------------
    // Real, current (sold today on reolink.com's official store), PoE/NVR-
    // compatible models only — deliberately excludes Reolink's battery/WiFi/
    // solar Argus and Altas lines (see file header). Researched 2026-07-30.
    // Bullet:
    {
        brand: 'Reolink', model: 'RLC-811A', product: 'RLC-811A — Smart 4K PoE Camera with 5× Optical Zoom', subtype: 'bullet',
        priceUsd: 110.59, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160). Motorized 5x optical zoom (f=2.7-13.5mm); FOV horizontal 105°-31°, vertical 55°-17° (no diagonal published). Color night vision (5x spotlights) + IR to 30m; person/vehicle/animal detection; 2-way audio + siren; IP67.',
        imageUrl: 'https://reolink.com/wp-content/uploads/2021/03/050713171614928397.8822.png',
        source: 'reolink.com/us/product/rlc-811a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-1212A', product: 'RLC-1212A — Smart 12MP PoE Spotlight Camera with Color Night Vision', subtype: 'bullet',
        priceUsd: 129.99, poe: true, fovDegDiagonal: null,
        specs: '12MP (4512×2512). Fixed lens f=4.0mm; FOV horizontal 97°, vertical 50° (no diagonal published). Color night vision (1x 4.3W spotlight) + IR to 30m; person/vehicle/pet detection; 2-way audio + siren; IP67.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2022/01/210404441642737884.1225.png',
        source: 'reolink.com/us/product/rlc-1212a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-810A', product: 'RLC-810A — Smart 4K PoE Security Camera with 100ft Night Vision', subtype: 'bullet',
        priceUsd: 99.99, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160). Fixed lens f=4.0mm; FOV horizontal 87°, vertical 44° (no diagonal published). IR night vision to 100ft; person/vehicle/animal detection; audio recording (mic only); IP66.',
        imageUrl: 'https://reolink.com/wp-content/assets/2020/07/rlc-810a-340.png',
        source: 'reolink.com/us/product/rlc-810a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-510A', product: 'RLC-510A — Smart 5MP PoE Security Camera', subtype: 'bullet',
        priceUsd: 69.99, poe: true, fovDegDiagonal: null,
        specs: 'Entry-level PoE bullet. 5MP (2560×1920). Fixed lens f=4.0mm; FOV horizontal 80°, vertical 42° (no diagonal published). IR night vision to 100ft (B/W only, no color night vision); person/vehicle/animal detection; IP67.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/assets/2020/07/rlc-510a-340.png',
        source: 'reolink.com/us/product/rlc-510a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'CX810', product: 'CX810 — ColorX 4K PoE Camera with True Full-Color Night Vision', subtype: 'bullet',
        priceUsd: 129.99, poe: true, fovDegDiagonal: 111.6,
        specs: '8MP/4K (3840×2160). ColorX f=4.0mm F1.0 super-aperture lens — diagonal FOV 111.6° (horizontal 93.2° / vertical 48.2°). True-color night vision via 4x spotlights (no IR/B&W mode); person/vehicle/animal detection; 2-way audio; IP67. Reolink\'s "CX-" sub-line, not "RLC-" prefixed, but a real current PoE bullet camera.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2024/04/CX810-1pack.png',
        source: 'reolink.com/product/cx810/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'CX410', product: 'CX410 — 2K ColorX PoE Camera with True Color Night Vision', subtype: 'bullet',
        priceUsd: 72.24, poe: true, fovDegDiagonal: null,
        specs: '4MP/2K (2560×1440). ColorX f=4mm F1.0 super-aperture lens; FOV horizontal 89°, vertical 46° (no diagonal published). True-color night vision via 4x spotlights, adjustable 3000K warm light; person/vehicle/animal detection; IP67.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2023/07/240659251690181965.3038.png',
        source: 'reolink.com/product/cx410/, confirmed 2026-07-30',
    },
    // Dome (fixed dome — the true PTZ speed-domes below are kept under
    // subtype 'ptz', matching this app's own camera_subtype semantics, even
    // though Reolink's own housing looks similar):
    {
        brand: 'Reolink', model: 'RLC-843A', product: 'RLC-843A — Smart 4K PoE IK10 Camera with 5X Optical Zoom', subtype: 'dome',
        priceUsd: 118.99, poe: true, fovDegDiagonal: 123,
        specs: '8MP/4K (3840×2160). IK10 vandal-proof motorized dome, 5x optical zoom — diagonal FOV 123° (wide) to 39° (full zoom).',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2023/09/121002511694512971.594.jpg',
        source: 'reolink.com/us/product/rlc-843a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-1224A', product: 'RLC-1224A — Smart 12MP UHD PoE Security Camera with Spotlight', subtype: 'dome',
        priceUsd: 129.99, poe: true, fovDegDiagonal: null,
        specs: '12MP (4512×2512). Fixed-lens turret/dome; FOV horizontal 93°, vertical 49° (no diagonal published). Color night vision with spotlight; person/vehicle/pet detection.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/assets/tespecs/rlc-1224a-1.png',
        source: 'reolink.com/us/product/rlc-1224a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-820A', product: 'RLC-820A — Smart 4K Ultra HD PoE Camera with Smart Detection', subtype: 'dome',
        priceUsd: 84.99, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160). Fixed-lens dome; FOV horizontal 87°, vertical 44° (no diagonal published). 100ft night vision; person/vehicle/animal detection.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/assets/tespecs/rlc-820a-1.png',
        source: 'reolink.com/us/product/rlc-820a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-1240A', product: 'RLC-1240A — 12MP IK10 Vandal-Proof Wide-FOV Security Camera', subtype: 'dome',
        priceUsd: 109.19, poe: true, fovDegDiagonal: 145,
        specs: '12MP (4512×2512). IK10 vandal-proof dome — diagonal FOV 145° (horizontal 121° / vertical 63.4°), widest FOV in this catalog. Color night vision.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2024/04/rlc-1240a-1.jpg',
        source: 'reolink.com/product/rlc-1240a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-840A', product: 'RLC-840A — 4K IK10 Vandal-Proof PoE Camera with Color Night Vision', subtype: 'dome',
        priceUsd: 107.99, poe: true, fovDegDiagonal: 125,
        specs: '8MP/4K (3840×2160). IK10 vandal-proof dome — diagonal FOV 125° (horizontal 105° / vertical 56°). Color night vision.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2023/12/200903231703063003.529.jpg',
        source: 'reolink.com/product/rlc-840a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-520A', product: 'RLC-520A — 5MP PoE IP Camera', subtype: 'dome',
        priceUsd: 69.99, poe: true, fovDegDiagonal: null,
        specs: 'Entry-level dome. 5MP (2560×1920). Fixed lens; FOV horizontal 80°, vertical 48° (no diagonal published). Person/vehicle/animal detection.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/assets/tespecs/rlc-520a-1.png',
        source: 'reolink.com/us/product/rlc-520a/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-833A', product: 'RLC-833A — 4K UHD PoE Camera with 3X Optical Zoom and Color Night Vision', subtype: 'dome',
        priceUsd: 109.99, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160). Motorized 3x optical zoom dome; FOV horizontal 94°-50°, vertical 53°-30° (no diagonal published). Color night vision. NOTE: showing "Notify Me" (temporarily backordered) on the live store as of 2026-07-30 — product page still active/current, not discontinued; confirm stock before quoting.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/assets/rlc-833a-1.png',
        source: 'reolink.com/product/rlc-833a/, confirmed 2026-07-30',
    },
    // PTZ (motorized pan-tilt-zoom — camera_subtype: 'ptz'):
    {
        brand: 'Reolink', model: 'RLC-823S1', product: 'RLC-823S1 — 4K 360° Auto-Tracking PoE PTZ Camera with 5X Optical Zoom', subtype: 'ptz',
        priceUsd: 289.99, poe: true, fovDegDiagonal: 123,
        specs: '8MP/4K (3840×2160). 360° endless pan / 90° tilt, 5x optical zoom, auto-tracking (person/vehicle/animal) — diagonal FOV 123° (wide) to 39° (full zoom). Color night vision; 2-way audio.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2024/04/RLC-823S1.jpg',
        source: 'reolink.com/product/rlc-823s1/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'RLC-823S2', product: 'RLC-823S2 — Smart 4K PTZ PoE Camera with 16X Optical Zoom', subtype: 'ptz',
        priceUsd: 379.99, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160). 360° pan / 90° tilt, 16x optical zoom, auto-tracking — FOV horizontal 54.2°-3.6°, vertical 41.7°-2.1° (no diagonal published). Color night vision (built-in spotlights); 2-way audio. No genuinely verifiable product photo found (Reolink\'s CDN served every candidate image as application/octet-stream, not image/*).',
        imageUrl: null,
        source: 'reolink.com/product/rlc-823s2/, confirmed 2026-07-30',
    },
    {
        brand: 'Reolink', model: 'TrackMix PoE', product: 'TrackMix PoE — 4K Dual-Lens PoE PTZ Camera with Auto Tracking', subtype: 'ptz',
        priceUsd: 169.99, poe: true, fovDegDiagonal: null,
        specs: '8MP/4K (3840×2160), dual-lens (wide f=2.8mm + telephoto f=8mm) — fixed wide view plus a simultaneously-zooming tracking lens, 6x hybrid (optical+digital) zoom. FOV horizontal 104°-38°, vertical 60°-21° (no diagonal published). 355° pan / 0-90° tilt; auto-tracking; color/IR night vision (2 spotlights). No plain product photo found with a real image/* content-type — only a features/specs graphic exists, so imageUrl is left unset rather than showing a non-product image.',
        imageUrl: null,
        source: 'reolink.com/product/reolink-trackmix-poe/, confirmed 2026-07-30',
    },
    // Multi-sensor (several independently-aimed lenses in one housing —
    // camera_subtype: 'multi_sensor'). fovDegDiagonal is deliberately null
    // here for the SAME full-circle-sentinel reason as UVC-G6-Pro-360 above
    // (see file header) — this subtype's fov_deg:360 default means "render
    // a full circle," not a literal optical angle, so a per-lens FOV spec
    // must never auto-apply over it.
    {
        brand: 'Reolink', model: 'OMVI 3i PoE', product: 'OMVI 3i PoE — Triple-Lens 360° PoE Security Camera', subtype: 'multi_sensor',
        priceUsd: 329.99, poe: true, fovDegDiagonal: null,
        specs: '18MP combined across 3 lenses: upper dual-lens panoramic module (10MP, 5120×1920, FOV horizontal 180° / vertical 65°) + lower single pan-tilt lens (8MP/4K, 3840×2160, FOV horizontal 55° / vertical 30°, 350° pan / 50° tilt range). Requires PoE+ (802.3at, 48V active) or DC 12V/2A; NVR firmware must be v3.6.5+ to show the PT lens view, not just the dual-lens panoramic feed. Product image returned HTTP 200 but with no image/* content-type header (a Reolink CDN gap, same issue as RLC-823S2/TrackMix PoE above) — genuinely a real photo (confirmed via `file`, a valid 400×400 PNG) but left unset here to keep this catalog\'s own verification bar consistent rather than making an exception.',
        imageUrl: null,
        source: 'reolink.com/product/omvi-3i-poe/ + Amazon listing (ASIN B0GF7X4M1Y, price cross-check), confirmed 2026-07-30',
    },
    // Doorbell:
    {
        brand: 'Reolink', model: 'D340P', product: 'Video Doorbell PoE (2K+ Wired Video Doorbell with Chime)', subtype: 'doorbell',
        priceUsd: 119.99, poe: true, fovDegDiagonal: 180,
        specs: '5MP/2K+ (2560×1920). Diagonal FOV 180° (horizontal 100° / vertical 135°). Ships WITH an indoor chime included in the box — no separate chime purchase needed. Local microSD recording (up to 256GB) or an optional Reolink NVR/Home Hub Pro — no subscription required. Person/package/motion AI detection; 2-way audio.',
        imageUrl: 'https://home-cdn.reolink.us/wp-content/uploads/2023/07/060323501688613830.9308.png',
        source: 'reolink.com/product/reolink-video-doorbell-poe/, confirmed 2026-07-30',
    },
];

// True when a camera_model string is a real, currently-priced catalog
// entry — shared by estimateDeviceCost()/DeviceEditPanel.js so both agree
// on what counts as "a real model" without re-deriving the lookup.
export function findCameraCatalogEntry(model) {
    return model ? CAMERA_CATALOG.find((c) => c.model === model) : null;
}

// Every real camera model, across every brand.
export function trueCameras() {
    return CAMERA_CATALOG;
}

// Real camera models matching one camera_subtype — what DeviceEditPanel.js's
// model picker actually shows, since a picker offering every brand/form
// factor at once regardless of the currently-pressed subtype button would
// let staff attach a bullet-shaped model to a "PTZ" device.
export function camerasBySubtype(subtype) {
    return CAMERA_CATALOG.filter((c) => c.subtype === subtype);
}
